<template>
  <section class="sport-hero-banner show-on-load">
    <div v-if="$isPageEditing">
      <sport-hero-banner-item :fields="fields" />
      <sc-placeholder :name="PLACEHOLDER" :rendering="sportHeroBannerRendering" />
    </div>
    <div v-else class="sport-hero-banner__container">
      <template v-for="(item, index) in items" :key="index">
        <Transition name="sport-hero-banner">
          <sport-hero-banner-item v-if="index === currentItemIndex" :fields="item" />
        </Transition>
      </template>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { Placeholder as ScPlaceholder } from '@sitecore-jss/sitecore-jss-vue';
import type { ComponentRendering } from '@sitecore-jss/sitecore-jss-vue';
import type { SportEventListFields, SportHeroBannerItemFields } from '../types';
import type { SportHeroBannerPlaceholderItem } from './types';
import SportHeroBannerItem from '../SportHeroBannerItem/SportHeroBannerItem.vue';

const PLACEHOLDER = 'sport-hero-banner-items';

const props = withDefaults(
  defineProps<{
    fields?: SportEventListFields;
    sportHeroBannerRendering?: ComponentRendering;
    params?: Record<string, string>;
    activeSlide?: string;
  }>(),
  {
    fields: () => ({}),
    params: () => ({}),
    activeSlide: '',
  }
);

const items = computed((): SportHeroBannerItemFields[] => {
  const f = props.fields;
  const defaultItem: SportHeroBannerItemFields = {
    description: f.description,
    imageDesktop: f.imageDesktop,
    imageMobile: f.imageMobile,
    imageTablet: f.imageTablet,
    logo1: f.logo1,
    logo2: f.logo2,
    subtitle: f.subtitle,
    title: f.title,
  };
  const placeholderItems =
    (
      props.sportHeroBannerRendering?.placeholders?.[PLACEHOLDER] as
        | SportHeroBannerPlaceholderItem[]
        | undefined
    )
      ?.filter((item) => !!item.fields)
      .map((item) => item.fields as SportHeroBannerItemFields) ?? [];
  return [defaultItem, ...placeholderItems];
});

const currentItemIndex = computed(() => {
  const index = items.value.findIndex(
    (item) => item.sportOrTournamentReference?.value === props.activeSlide
  );
  return Math.max(index, 0);
});
</script>

<style lang="scss">
@import './scss/SportHeroBanner.scss';
</style>
